import type { GoalCategory } from './events.js';
import type { StudentPath } from './flavour.js';

/**
 * A goal somebody can start in one tap, already broken into parts.
 *
 * The single highest-value thing this app can pre-load. Breaking "HSC
 * Physics" into its twenty-two chapters is real work, it is identical for
 * every HSC candidate in the country, and a student who has to type it out
 * will instead start a goal with no parts at all — which is the version of a
 * goal that does nothing, because progress here is measured in parts.
 *
 * THE CONTENT LIVES IN THE APP, NOT HERE. Chapter names are locale-specific
 * data, and `@aj/core` has no `t()` by design. Core owns the shape and the
 * lookup; `apps/pwa` owns the packs and hands them in already in the reader's
 * language. That is the same split the design system and the coach use.
 */
export type GoalPreset = {
  /** Stable across renames and translations — this is what `Goal.preset`
   *  stores, so a pack can be re-worded without orphaning existing goals. */
  id: string;
  /** Who it is offered to. A pack for `hsc` is noise for everyone else. */
  path: StudentPath;
  category: GoalCategory;
  /** Already translated. */
  title: string;
  /** Already translated, in syllabus order. */
  parts: string[];
};

/** Everything offered to one kind of student. Order is offer order. */
export function presetsFor(packs: readonly GoalPreset[], path: StudentPath): GoalPreset[] {
  return packs.filter((preset) => preset.path === path);
}
